import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FiSearch, 
  FiFilter, 
  FiDownload, 
  FiEye, 
  FiEdit, 
  FiTrash2,
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiUsers,
  FiUserCheck,
  FiUserPlus
} from "react-icons/fi";
import { 
  FaUserCircle, 
  FaCity, 
  FaBuilding,
  FaStar,
  FaCrown,
  FaMedal
} from "react-icons/fa";
import { sendApiRequest } from "../../utils";
import { toast } from "react-toastify";
import CustomerDetailsModal from "./CustomerDetailsModal";
import { CSVLink } from "react-csv";
import EditCustomerModal from "./EditCustomerModal";

// Helper function to convert date to DD-MM-YYYY format
const formatDateForCSV = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const AllCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [customersPerPage] = useState(10);
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    city: "",
    state: "",
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);

  // Fetch customers data
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const response = await sendApiRequest("api/admin/customers", {}, "GET");
        if (response.status === 200) {
          setCustomers(response.data.data);
          setFilteredCustomers(response.data.data);
        } else {
          toast.error("Failed to fetch customers");
        }
      } catch (error) {
        console.error("Error fetching customers:", error);
        toast.error("Error fetching customers");
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  // Filter and search customers
  useEffect(() => {
    let result = [...customers];

    if (searchTerm) {
      result = result.filter(
        (customer) =>
          customer.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.customerPhone?.includes(searchTerm) ||
          customer.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.state?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filters.city) {
      result = result.filter(
        (customer) => customer.city?.toLowerCase() === filters.city.toLowerCase()
      );
    }
    if (filters.state) {
      result = result.filter(
        (customer) => customer.state?.toLowerCase() === filters.state.toLowerCase()
      );
    }

    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === "createdAt") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredCustomers(result);
    setCurrentPage(1);
  }, [customers, searchTerm, filters, sortField, sortDirection]);

  const uniqueCities = [...new Set(customers.map((customer) => customer.city).filter(Boolean))];
  const uniqueStates = [...new Set(customers.map((customer) => customer.state).filter(Boolean))];

  const indexOfLastCustomer = currentPage * customersPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;
  const currentCustomers = filteredCustomers.slice(
    indexOfFirstCustomer,
    indexOfLastCustomer
  );
  const totalPages = Math.ceil(filteredCustomers.length / customersPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const clearFilters = () => {
    setFilters({ city: "", state: "" });
    setSearchTerm("");
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleViewCustomer = (customerId) => {
    const customer = customers.find((c) => c._id === customerId);
    if (customer) {
      setSelectedCustomer(customer);
      setIsModalOpen(true);
    }
  };

  const handleEditCustomer = async (updatedCustomer) => {
    try {
      const response = await sendApiRequest(
        `api/admin/customers/${editCustomer._id}`,
        updatedCustomer,
        "PUT"
      );
      if (response.success) {
        toast.success("Customer updated successfully");
        setCustomers(customers.map(customer => 
          customer._id === editCustomer._id ? { ...customer, ...updatedCustomer } : customer
        ));
        setIsEditModalOpen(false);
      } else {
        toast.error("Failed to update customer");
      }
    } catch (error) {
      console.error("Error updating customer:", error);
      toast.error("Error updating customer");
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (window.confirm("Are you sure you want to delete this customer?")) {
      try {
        const response = await sendApiRequest(
          `api/admin/customers/${customerId}`,
          {},
          "DELETE"
        );
        if (response.status === 200) {
          toast.success("Customer deleted successfully");
          setCustomers(customers.filter((customer) => customer._id !== customerId));
        } else {
          toast.error("Failed to delete customer");
        }
      } catch (error) {
        console.error("Error deleting customer:", error);
        toast.error("Error deleting customer");
      }
    }
  };

  const openEditModal = (customer) => {
    setEditCustomer(customer);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditCustomer(null);
  };

  const getRankIcon = (name) => {
    const firstChar = name?.charAt(0).toUpperCase() || 'A';
    const colors = ['from-indigo-500 to-purple-500', 'from-emerald-500 to-teal-500', 'from-amber-500 to-orange-500', 'from-blue-500 to-cyan-500', 'from-pink-500 to-rose-500'];
    const colorIndex = firstChar.charCodeAt(0) % colors.length;
    return { initial: firstChar, gradient: colors[colorIndex] };
  };

  const csvData = {
    headers: [
      { label: "Name", key: "customerName" },
      { label: "Email", key: "customerEmail" },
      { label: "Phone", key: "customerPhone" },
      { label: "City", key: "city" },
      { label: "State", key: "state" },
      { label: "Registration Date", key: "createdAt" }
    ],
    data: filteredCustomers.map(customer => ({
      ...customer,
      createdAt: formatDateForCSV(customer.createdAt)
    })),
    filename: `customers-export-${new Date().toISOString().split('T')[0]}.csv`
  };

  const stats = {
    total: customers.length,
    active: customers.filter(c => c.status !== 'inactive').length,
    newThisMonth: customers.filter(c => {
      const date = new Date(c.createdAt);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  All Customers
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">Manage and view all registered customers</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.location.reload()}
                className="p-2.5 bg-white rounded-xl shadow-md hover:shadow-lg transition text-gray-600 hover:text-indigo-600"
                title="Refresh"
              >
                <FiRefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Customers</p>
                <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
                <p className="text-xs text-gray-400 mt-1">All registered users</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                <FiUsers className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Customers</p>
                <p className="text-3xl font-bold text-emerald-600">{stats.active}</p>
                <p className="text-xs text-gray-400 mt-1">Currently active</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                <FiUserCheck className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">New This Month</p>
                <p className="text-3xl font-bold text-amber-600">{stats.newThisMonth}</p>
                <p className="text-xs text-gray-400 mt-1">Joined this month</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                <FiUserPlus className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6 overflow-hidden">
          <div className="p-5">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, phone, city or state..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600 transition"
                >
                  <FiFilter className="w-4 h-4" />
                  Filters
                  {showFilters ? <FiChevronRight className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
                </button>
                <CSVLink
                  headers={csvData.headers}
                  data={csvData.data}
                  filename={csvData.filename}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg transition"
                >
                  <FiDownload className="w-4 h-4" />
                  Export CSV
                </CSVLink>
              </div>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-gray-100"
                >
                  <select
                    name="city"
                    value={filters.city}
                    onChange={handleFilterChange}
                    className="px-4 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none bg-white"
                  >
                    <option value="">All Cities</option>
                    {uniqueCities.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>

                  <select
                    name="state"
                    value={filters.state}
                    onChange={handleFilterChange}
                    className="px-4 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none bg-white"
                  >
                    <option value="">All States</option>
                    {uniqueStates.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>

                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 px-4 py-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                  >
                    <FiX className="w-4 h-4" />
                    Clear Filters
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-4 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Showing {filteredCustomers.length > 0 ? indexOfFirstCustomer + 1 : 0} to{' '}
            {Math.min(indexOfLastCustomer, filteredCustomers.length)} of {filteredCustomers.length} customers
          </p>
        </div>

        {/* Customers Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <tr>
                  <th onClick={() => handleSort("customerName")} className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-indigo-600 transition">
                    Name {sortField === "customerName" && (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th onClick={() => handleSort("customerEmail")} className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-indigo-600 transition">
                    Email {sortField === "customerEmail" && (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th onClick={() => handleSort("customerPhone")} className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-indigo-600 transition">
                    Phone {sortField === "customerPhone" && (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th onClick={() => handleSort("city")} className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-indigo-600 transition">
                    Location {sortField === "city" && (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th onClick={() => handleSort("createdAt")} className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-indigo-600 transition">
                    Joined {sortField === "createdAt" && (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="px-4 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <AnimatePresence>
                  {loading ? (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <td colSpan="6" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="relative">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <FiUsers className="w-5 h-5 text-indigo-400 animate-pulse" />
                            </div>
                          </div>
                          <p className="mt-4 text-gray-500 font-medium">Loading customers...</p>
                        </div>
                       </td>
                    </motion.tr>
                  ) : currentCustomers.length === 0 ? (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <td colSpan="6" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <FiUsers className="w-10 h-10 text-gray-400" />
                          </div>
                          <p className="text-gray-500 font-medium text-lg">No customers found</p>
                          <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
                        </div>
                       </td>
                    </motion.tr>
                  ) : (
                    currentCustomers.map((customer, index) => {
                      const rank = getRankIcon(customer.customerName);
                      return (
                        <motion.tr
                          key={customer._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ delay: index * 0.02 }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${rank.gradient} flex items-center justify-center text-white font-bold shadow-md`}>
                                {rank.initial}
                              </div>
                              <span className="font-medium text-gray-800">{customer.customerName}</span>
                            </div>
                           </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 text-gray-600">
                              <FiMail className="w-3 h-3 text-gray-400" />
                              <span className="text-sm">{customer.customerEmail}</span>
                            </div>
                           </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 text-gray-600">
                              <FiPhone className="w-3 h-3 text-gray-400" />
                              <span className="text-sm">{customer.customerPhone}</span>
                            </div>
                           </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 text-gray-600">
                              <FiMapPin className="w-3 h-3 text-gray-400" />
                              <span className="text-sm">{customer.city}, {customer.state}</span>
                            </div>
                           </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 text-gray-500 text-sm">
                              <FiCalendar className="w-3 h-3 text-gray-400" />
                              {formatDate(customer.createdAt)}
                            </div>
                           </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleViewCustomer(customer._id)}
                                className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-600 transition"
                                title="View Details"
                              >
                                <FiEye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openEditModal(customer)}
                                className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-amber-100 hover:text-amber-600 transition"
                                title="Edit Customer"
                              >
                                <FiEdit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteCustomer(customer._id)}
                                className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-600 transition"
                                title="Delete Customer"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                           </td>
                        </motion.tr>
                      );
                    })
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-gray-100 px-6 py-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <CustomerDetailsModal
        customer={selectedCustomer}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCustomer(null);
        }}
      />

      <EditCustomerModal
        customer={editCustomer}
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        onUpdate={handleEditCustomer}
      />
    </div>
  );
};

export default AllCustomers;